/**
 * The ONE client-contract vocabulary — what a running client and a server share about WHICH build is running and
 * WHICH contract level it speaks (WEB-APP-VERSION-REFRESH-001).
 *
 * TWO DIFFERENT QUESTIONS, two different answers:
 *
 *   - "Is a newer build deployed?" — the BUILD IDENTITY. Every web build names itself (a hash of every file it
 *     emits; `@puffin-core/shared-config`'s `puffin:build-identity` plugin) in `<meta name="app-build-id">`
 *     and in `version.json`; a running tab compares the two. A hash is unordered: it only says "different".
 *     The metas name no platform (owner ruling Q-D4-BUILD-ID-META, 2026-10-21: every page carries them, a
 *     tenant's own site included); only the build's own code reads them, so the name never crosses builds.
 *
 *   - "Is this client too old for this route?" — the CONTRACT LEVEL. An ordered integer per client kind, raised
 *     by the change that ships a client behaviour a server will later require (the first: money mutations that
 *     send `Idempotency-Key`). Every request declares `X-Client-Contract: <kind>/<level>`; a server's roster may
 *     name a minimum level per route family and refuse an older client with `426 CLIENT_TOO_OLD`, which a web
 *     tab turns into "reload" and the native app into "update from the store". A request that declares NO kind
 *     (another server, a webhook, a CLI) is never judged by it.
 *
 * Raising a level is half of a two-train change (GUIDES/shared/standards/deploy-compatibility.md): the client
 * ships the behaviour and the new level first; a server requires the level only in a later train.
 *
 * Data only, zero dependencies; dual-use: CJS source with a GENERATED ESM re-export.
 *
 * @module @puffin-core/client-contract
 */

'use strict';

/**
 * Where a web build carries its identity: the HTML entry's meta tags (the id, and when the build ran — the footer
 * shows both, owner ruling "Code and date") and the file a tab polls.
 */
const BUILD_IDENTITY = Object.freeze({
  META_NAME: 'app-build-id',
  TIME_META_NAME: 'app-build-time',
  VERSION_FILE: 'version.json',
});

/** The client kinds that declare themselves. Anything else is not a client this contract judges. */
const CLIENT_KIND = Object.freeze({ WEB: 'web', NATIVE: 'native' });

/**
 * The request headers a client sends: its contract (`<kind>/<level>`), for the web its build id, and on a WRITE the
 * realtime connection the write was made from (APPROVALS-STORE-ROOMS-001 U7, owner ruling R-UAT2-26: the server's
 * emit for that write comes back stamped with it, and that ONE session — never another tab of the same user — skips
 * its own echo).
 */
const CLIENT_HEADERS = Object.freeze({
  CONTRACT: 'X-Client-Contract',
  BUILD: 'X-Client-Build',
  REALTIME_ORIGIN: 'X-Realtime-Origin',
});

/** The refusal a server answers a too-old declared client with. */
const CLIENT_TOO_OLD = Object.freeze({ code: 'CLIENT_TOO_OLD', status: 426 });

/** The contract level each client kind ships TODAY. Raised only together with a ledger entry below. */
const CLIENT_CONTRACT_LEVELS = Object.freeze({ web: 5, native: 1 });

/** What each level added, per kind, from 1 to the current level (a gap or a level ahead is a test failure). */
const CLIENT_CONTRACT_LEDGER = Object.freeze({
  web: Object.freeze([
    Object.freeze({
      level: 1,
      added:
        'The client declares its contract level and build id on every request, and reloads at a safe moment ' +
        'when a newer build is deployed or a server refuses it as too old.',
    }),
    Object.freeze({
      level: 2,
      added:
        "pos-web's register quotes a gift card and the sale spends it: a gift-card tender names its card " +
        '(giftCardId), and the register never calls POST /v1/gift-cards/:giftCardId/redeem (COMPLIANCE-REPORTING-001 ' +
        'F-CR-3 unit 2).',
    }),
    // Push 17's ONE shared web level (GAMING's ruling, 2026-10-22: one level per push train, every change it covers
    // named on its own line with its READY sha). Assigned 3 at the push-17 assembly's join (2026-10-23): it covers D4's
    // fulfillment choice and D6's confirm-payment possession proof.
    Object.freeze({
      level: 3,
      added:
        'The storefront checkout names the buyer’s delivery choice on place-order — `fulfillment`: shipping, or ' +
        'pickup at one store (LIFE-SHORTLISTED-001 U15 A3, READY 21 `24c943b408`). Train N of deploy-compatibility ' +
        '§6: purchase-api requires it on `POST /v1/checkout/place-order` only in a later train.\n' +
        "The storefront's confirm presents its PaymentIntent client secret: POST /v1/checkout/confirm-payment names " +
        '{ orderId, clientSecret }, the proof the shopper holds the order (REGISTER-WEB-ORDERS-001, READY `e49ffcf78e`; ' +
        "shipped with the server's requirement in ONE train, a security exception to deploy-compatibility §6, " +
        'rulings.md 2026-10-23).',
    }),
    // Push 18's ONE shared web level (GAMING's ruling, 2026-10-22). 4 is PROVISIONAL: the joiner renumbers it only if
    // another push-18 lane also takes 4.
    Object.freeze({
      level: 4,
      added:
        'The order clients read and send the ONE order-line table and money family (owner ruling Q-RWO-1): a line is ' +
        'named by `lineItemId` (an `order_line_items` row) and order money by the `*Amount` family (`taxAmount`, ' +
        '`discountAmount`, `shippingAmount`). Read on GET /v1/orders, GET /v1/orders/:id, its stock-returns and ' +
        'stock-shortfalls, and the shopper’s GET /v1/account/orders and /:orderId; answered by the shortfall ' +
        'backorder / fill, the shopper’s cancel, and the checkout’s calculate-tax and place-order; sent as ' +
        'stock-decision lines on POST /v1/orders/:id/stock-returns and /cancel, the PATCH /v1/orders/:id/payment ' +
        'refund, and the platform POST /v1/platform/tenants/:tenantId/orders/:orderId/refund and /cancel ' +
        '(REGISTER-WEB-ORDERS-001 A4, READY `58a1bc7111`). Train N of deploy-compatibility §6: purchase-api refuses an ' +
        'older web client on exactly these routes in a later train (push 19, ' +
        '`apps/purchase-api/src/constants/minimumClientContractRoutes.js`).' +
        '\n' +
        'A register beyond what the account includes is SHOWN, then accepted: pos-api answers 409 ' +
        'PRICE_CONFIRMATION_REQUIRED with the engine’s quote (in place of REGISTER_CHARGE_CONFIRMATION_REQUIRED, also ' +
        'in a device registration’s registerRefusal), and POST /v1/terminals/register accepts only the echoed quote, ' +
        '`acceptedCharge: { amountCents, billingCycle }` (MAILBOX-SIGNUP-JOURNEY-001 B3, READY sha at the join).',
    }),
    // Push 19's ONE shared web level (GAMING's ruling, 2026-10-22: one level per push train, each change on its own
    // line). Push 19's joiner keeps ONE level 5 and unions every lane's line into it.
    Object.freeze({
      level: 5,
      added:
        "port-web's mailbox holder enrolment names no box number: POST /v1/port/mailbox-holder-subscriptions no " +
        'longer carries `holder.mailboxNumber` — port-api enrols the box pos holds for the mail name ' +
        '(MAILBOX-SIGNUP-JOURNEY-001 G1c, READY sha at the join; GAMING ruling (a)). Train N of deploy-compatibility ' +
        '§6: port-api accepts an older tab’s box number and ignores it; it refuses it, and gates the route on this ' +
        'level, only in a later train (push 20, G1d).' +
        '\n' +
        "pos-web's store checklist names its store on GET /v1/checklist-use-cases/:slug/template " +
        '(`?locationId=`), so a store’s own checklist answers (owner ruling Q-E8B-1 "Store override"; ' +
        'POS-PRINT-LAYOUTS-001 M1.E8B, READY sha at the join). Train N of deploy-compatibility §6: pos-api requires ' +
        'it, and refuses an older web client 426, only in a later train.' +
        '\n' +
        "pos-web's ship-a-package wizard sends a shipment's boxes, `packages: [{weight, dimensions, packagingTraits, " +
        "packaging}]`, on POST /v1/shipping/shipments/quote, and reads each priced service's `packages` (each box's " +
        'billed weight, charges and price; owner ruling "Full multi-piece", POS-PRINT-LAYOUTS-001 M1.5 B6, READY sha ' +
        'at the join). The route replaces the one-package shape in this train (its exposure measured 0, DYLAN-PC ' +
        '2026-10-26); the 426 roster entry for the route follows in push 20 (deploy-compatibility §6).' +
        '\n' +
        "pos-web's register sale names no order statuses: POST /v1/orders no longer carries status, paymentStatus and " +
        'fulfillmentStatus — pos-api writes them from the order’s source, in the ONE order vocabulary ' +
        '(REGISTER-WEB-ORDERS-001 B1, ORD1). Train N of deploy-compatibility §6: pos-api accepts an older tab’s three ' +
        'and drops them unread; it refuses them, and gates POST /v1/orders on this level, only in a later train.\n' +
        "purchase-web's admin refunds an order through its own route, POST /v1/orders/:id/refund, never " +
        'PATCH /v1/orders/:id/payment (REGISTER-WEB-ORDERS-001 B2, owner ruling D-B2-2). Train N of ' +
        'deploy-compatibility §6: purchase-api keeps the PATCH’s refund arm for an older tab, and removes the PATCH ' +
        '(refusing older clients) only in a later train.',
    }),
  ]),
  native: Object.freeze([
    Object.freeze({
      level: 1,
      added:
        'The native app declares its contract level on every request, and asks the user to update from the ' +
        'store when a server refuses it as too old.',
    }),
  ]),
});

const KINDS = Object.values(CLIENT_KIND);
const HEADER_VALUE = /^([a-z]+)\/([1-9][0-9]*)$/;

/**
 * @param {*} value
 * @returns {boolean} whether `value` names a client kind this contract knows
 */
function isClientKind(value) {
  return KINDS.includes(value);
}

/**
 * The `X-Client-Contract` value for a client kind at a level.
 *
 * @param {string} kind - one of {@link CLIENT_KIND}
 * @param {number} level - a positive integer
 * @returns {string} `<kind>/<level>`
 * @throws {Error} on an unknown kind or a level that is not a positive integer
 */
function formatClientContract(kind, level) {
  if (!isClientKind(kind)) {
    throw new Error(
      `formatClientContract: unknown client kind "${kind}" (expected ${KINDS.join(' or ')}).`,
    );
  }
  if (!Number.isInteger(level) || level < 1) {
    throw new Error(`formatClientContract: the level must be a positive integer (got ${level}).`);
  }
  return `${kind}/${level}`;
}

/**
 * Read an `X-Client-Contract` header.
 *
 * @param {string|string[]|undefined} value - the header as the server received it
 * @returns {null|{kind: string, level: number}|{unreadable: string}} `null` when the client declared nothing;
 *   the kind and level when readable; `{ unreadable }` (the raw value) when a value was sent but cannot be read,
 *   so a server names it rather than guess a level
 */
function parseClientContract(value) {
  if (value === undefined || value === '') return null;
  const raw = Array.isArray(value) ? value.join(',') : String(value);
  const match = HEADER_VALUE.exec(raw);
  if (!match || !isClientKind(match[1])) return { unreadable: raw };
  return { kind: match[1], level: Number(match[2]) };
}

module.exports = {
  BUILD_IDENTITY,
  CLIENT_KIND,
  CLIENT_HEADERS,
  CLIENT_TOO_OLD,
  CLIENT_CONTRACT_LEVELS,
  CLIENT_CONTRACT_LEDGER,
  isClientKind,
  formatClientContract,
  parseClientContract,
};
